import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  MagnifyingGlass, User, EnvelopeSimple, Phone, FolderSimple, Eye, ArrowRight, ArrowsClockwise, LinkSimple,
} from "@phosphor-icons/react";
import ClientProfileDialog from "@/components/app/ClientProfileDialog";

/**
 * Firm-wide "Clients" page (`/clients`). Solicitor + admin gated.
 *
 *   - Searchable list of every client account (name / email / phone) with a
 *     compact case count + active count.
 *   - Click any row to open a read-only profile drawer surfacing every
 *     linked case + that client's per-case extended details (DOB, AML,
 *     address, ID, source of funds, mortgage status, notes, etc.).
 *   - Click any linked case in the drawer or the count popover to jump
 *     straight to /cases/{id}.
 */
export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewClientId, setViewClientId] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "solicitor") {
      toast.error("Solicitor access required");
      navigate("/");
    }
  }, [user, navigate]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/clients");
      setClients(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (user?.is_admin) fetchClients(); }, [fetchClients, user]);
  // Refetch when the tab regains focus so a client who just signed up in
  // another tab (or invited from a case page that triggered registration)
  // shows up without a manual refresh.
  useEffect(() => {
    if (!user?.is_admin) return undefined;
    const onFocus = () => { fetchClients(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, fetchClients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      c.name?.toLowerCase().includes(q)
      || c.email?.toLowerCase().includes(q)
      || (c.phone || "").toLowerCase().includes(q)
    );
  }, [search, clients]);

  const totals = useMemo(() => ({
    total: clients.length,
    active_users: clients.filter((c) => c.is_active !== false).length,
    with_cases: clients.filter((c) => c.cases_count > 0).length,
  }), [clients]);

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-sm text-slate-500">Admin access required.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clients-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Clients
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {totals.total} {totals.total === 1 ? "client" : "clients"} on file · {totals.active_users} active · {totals.with_cases} with linked cases
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <DiagnosticPopover />
            <button
              type="button"
              onClick={fetchClients}
              disabled={loading}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid="clients-refresh-btn"
              title="Refresh — pulls any new signups that happened since you opened this page"
            >
              <ArrowsClockwise weight="bold" className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <div className="relative flex-1 sm:w-80">
              <MagnifyingGlass weight="bold" className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or phone…"
                className="pl-9 h-9 text-sm"
                data-testid="clients-search"
              />
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400" data-testid="clients-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400" data-testid="clients-empty">
              {search ? `No clients matching "${search}".` : "No clients yet."}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Cases</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/50 cursor-pointer" data-testid={`clients-row-${c.id}`}
                    onClick={() => setViewClientId(c.id)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#100f5d" }}>
                          <span className="text-white text-xs font-bold">{c.name?.charAt(0)?.toUpperCase() || <User weight="bold" className="w-4 h-4 text-white" />}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                          {c.created_at && (
                            <p className="text-[10px] text-slate-400">
                              Since {new Date(c.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-600 inline-flex items-center gap-1">
                        <EnvelopeSimple weight="bold" className="w-3 h-3 text-slate-400" />
                        {c.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-600 inline-flex items-center gap-1">
                          <Phone weight="bold" className="w-3 h-3 text-slate-400" />
                          {c.phone}
                        </a>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {c.cases_count > 0 ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" data-testid={`clients-cases-pill-${c.id}`}>
                              <FolderSimple weight="bold" className="w-3 h-3" />
                              {c.cases_count} {c.cases_count === 1 ? "case" : "cases"}
                              {c.active_cases > 0 && (
                                <span className="ml-1 text-[10px] text-blue-500">· {c.active_cases} active</span>
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-2">
                            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 px-2">Linked cases</p>
                            <ul className="space-y-1 max-h-72 overflow-y-auto">
                              {c.cases.map((cs) => (
                                <li key={cs.id}>
                                  <button onClick={() => navigate(`/cases/${cs.id}`)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 transition-colors" data-testid={`clients-case-jump-${cs.id}`}>
                                    <p className="text-xs font-medium text-slate-900 truncate">{cs.title}</p>
                                    <p className="text-[10px] text-slate-500 truncate">{cs.property_address || "—"}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                        cs.status === "completed" ? "bg-emerald-50 text-emerald-700"
                                          : cs.status === "archived" ? "bg-slate-100 text-slate-500"
                                          : "bg-blue-50 text-blue-700"
                                      }`}>{cs.status}</span>
                                      {cs.case_role && (
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">{cs.case_role}</span>
                                      )}
                                    </div>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.is_active === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">Deactivated</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.magic_token && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = `${window.location.origin}/portal-login/${c.magic_token}`;
                              try {
                                navigator.clipboard.writeText(link)
                                  .then(() => toast.success("Portal sign-in link copied"))
                                  .catch(() => toast.error("Couldn't copy — please copy manually"));
                              } catch { toast.error("Clipboard not available"); }
                            }}
                            data-testid={`clients-copy-portal-${c.id}`}
                            title="Copy passwordless portal sign-in link"
                          >
                            <LinkSimple weight="bold" className="w-3.5 h-3.5" /> Portal link
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={(e) => { e.stopPropagation(); setViewClientId(c.id); }} data-testid={`clients-view-${c.id}`}>
                          <Eye weight="bold" className="w-3.5 h-3.5" /> View
                          <ArrowRight weight="bold" className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <ClientProfileDialog
        open={!!viewClientId}
        onOpenChange={(open) => !open && setViewClientId(null)}
        clientId={viewClientId}
      />
    </div>
  );
}


/**
 * Admin diagnostic popover — when a client signs up but doesn't appear
 * on the Clients list, this looks up their email directly against the
 * users collection and reports what's going on (role mismatch, pending-
 * only, never signed up, etc.) with a one-click fix-role button.
 */
function DiagnosticPopover() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("lookup");
  const [recent, setRecent] = useState(null);
  const [caseRef, setCaseRef] = useState("");
  const [caseRaw, setCaseRaw] = useState(null);
  // Per-pending-client-row "Attach registered user" input state, keyed by
  // the stored pending email so each row has its own field.
  const [attachInputs, setAttachInputs] = useState({});
  const setAttachFor = (key, val) =>
    setAttachInputs((prev) => ({ ...prev, [key]: val }));
  const attachUser = async (pendingEmail) => {
    const userEmail = (attachInputs[pendingEmail] || "").trim();
    if (!userEmail || !caseRaw?.case_id) return;
    setBusy(true);
    try {
      const { data } = await API.post(
        `/admin/cases/${caseRaw.case_id}/attach-user`,
        null,
        { params: { user_email: userEmail, pending_email: pendingEmail } },
      );
      toast.success(`Attached ${data.user_name || userEmail} to case ${data.case_ref || ""}`);
      setAttachFor(pendingEmail, "");
      // Reload raw case data so the row flips from pending -> linked.
      await loadCaseRaw();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Attach failed");
    } finally {
      setBusy(false);
    }
  };
  const lookup = async (mode = "auto") => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    setResult(null);
    try {
      // First try exact email lookup (whitespace-tolerant on the backend).
      // If nothing comes back AND the term looks like a name (no @), or
      // the caller forced partial mode, fall back to name+email partial.
      const looksLikeEmail = e.includes("@");
      if (mode === "partial" || !looksLikeEmail) {
        const { data } = await API.get("/admin/user-lookup", { params: { q: e } });
        setResult(data);
      } else {
        const { data } = await API.get("/admin/user-lookup", { params: { email: e } });
        if (!data.found) {
          // Auto-fallback to partial search using the part before @ — this
          // catches the typo / hidden-character case the user just hit.
          const local = e.split("@")[0];
          const fb = await API.get("/admin/user-lookup", { params: { q: local } });
          // Merge: surface both the original "not found" context AND the
          // candidates the partial search dug up.
          setResult({ ...data, _partial_fallback: fb.data });
        } else {
          setResult(data);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };
  const fixRole = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/admin/user-lookup/fix-role", null, { params: { email: result.email } });
      if (data.changed) {
        toast.success(`Role changed from ${data.from} → ${data.to}`);
        await lookup();
      } else {
        toast.info(data.message || "No change");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fix failed");
    } finally {
      setBusy(false);
    }
  };
  const loadRecent = async () => {
    setBusy(true);
    try {
      const { data } = await API.get("/admin/recent-signups", { params: { days: 30 } });
      setRecent(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load recent signups");
    } finally {
      setBusy(false);
    }
  };

  const loadCaseRaw = async () => {
    const r = caseRef.trim();
    if (!r) return;
    setBusy(true);
    setCaseRaw(null);
    try {
      const { data } = await API.get("/admin/case-clients", { params: { case_ref: r } });
      setCaseRaw(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Case lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
          data-testid="clients-diagnostic-btn"
          title="Look up a client by email to see why they're not appearing on the list"
        >
          <MagnifyingGlass weight="bold" className="w-3.5 h-3.5" />
          Missing client?
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-3 space-y-2 max-h-[80vh] overflow-y-auto" align="end">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200 pb-2">
          {[
            ["lookup", "Email / Name"],
            ["recent", "Recent signups"],
            ["case", "Raw case data"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setTab(k); if (k === "recent" && !recent) loadRecent(); }}
              className={`text-[11px] px-2 py-1 rounded transition-colors ${
                tab === k ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"
              }`}
              data-testid={`diagnostic-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "lookup" && (
          <>
            <p className="text-xs font-semibold text-slate-700">Look up a missing client</p>
            <p className="text-[10px] text-slate-500">Enter email <em>or</em> partial name. Partial search runs automatically if exact email isn't found.</p>
            <div className="flex gap-1.5">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && lookup()}
                placeholder="email@example.com OR Darja"
                className="h-8 text-xs"
                data-testid="diagnostic-email-input"
              />
              <Button size="sm" className="h-8 text-xs" onClick={() => lookup()} disabled={busy} data-testid="diagnostic-lookup-btn">
                {busy ? "…" : "Look up"}
              </Button>
            </div>
          </>
        )}

        {tab === "recent" && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Signups last 30 days</p>
              <button onClick={loadRecent} disabled={busy} className="text-[10px] text-slate-500 hover:text-slate-700 underline">
                {busy ? "…" : "Refresh"}
              </button>
            </div>
            {recent && (
              <div className="text-[11px] space-y-1">
                <p className="text-slate-500">{recent.count} account{recent.count === 1 ? "" : "s"} created. Lists every role — solicitor / client / referrer / etc.</p>
                <div className="max-h-64 overflow-y-auto space-y-1 mt-1">
                  {recent.users.map((u) => (
                    <div key={u.user_id} className="bg-slate-50 border border-slate-200 rounded p-1.5 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{u.name || "(no name)"}</p>
                        <p className="text-[10px] text-slate-500 truncate">{u.email} · <span className={u.role_is_client ? "text-emerald-700" : "text-rose-700"}>{u.role || "(no role)"}</span> · {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "-"}</p>
                      </div>
                      {!u.role_is_client && u.role && (
                        <Button
                          size="sm"
                          className="h-6 text-[10px] shrink-0"
                          onClick={async () => {
                            try {
                              const { data } = await API.post("/admin/user-lookup/fix-role", null, { params: { email: u.email } });
                              if (data.changed) {
                                toast.success(`${u.name}: ${data.from} → ${data.to}`);
                                loadRecent();
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Fix failed");
                            }
                          }}
                        >
                          → client
                        </Button>
                      )}
                    </div>
                  ))}
                  {recent.users.length === 0 && <p className="text-slate-400 italic text-center py-4">No signups in this window.</p>}
                </div>
              </div>
            )}
          </>
        )}

        {tab === "case" && (
          <>
            <p className="text-xs font-semibold text-slate-700">Inspect raw client emails on a case</p>
            <p className="text-[10px] text-slate-500">Surfaces hidden whitespace / Cyrillic look-alike characters that visual inspection would miss.</p>
            <div className="flex gap-1.5">
              <Input
                value={caseRef}
                onChange={(e) => setCaseRef(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadCaseRaw()}
                placeholder="SA/23014"
                className="h-8 text-xs"
                data-testid="diagnostic-case-ref-input"
              />
              <Button size="sm" className="h-8 text-xs" onClick={loadCaseRaw} disabled={busy} data-testid="diagnostic-case-load-btn">
                {busy ? "…" : "Inspect"}
              </Button>
            </div>
            {caseRaw && (
              <div className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 space-y-1 mt-2">
                <p className="font-semibold">{caseRaw.case_ref} — {caseRaw.title}</p>
                {(caseRaw.clients || []).map((c, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded p-1.5 text-[10px] space-y-0.5">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-slate-500 font-mono break-all">
                      "{c.email}"
                      <span className="ml-2 text-slate-400">[{c.email_len} chars{!c.email_ascii_only ? ", contains non-ASCII!" : ""}]</span>
                    </p>
                    <p className="text-slate-500">
                      pending: <span className={c.pending_account ? "text-rose-600" : "text-emerald-600"}>{String(c.pending_account)}</span>
                      {" · "}invite: {c.last_invite_status || "(none)"}
                      {c.invite_sent_count ? ` (${c.invite_sent_count}×)` : ""}
                      {" · "}role: {c.case_role}
                    </p>
                    {!c.email_ascii_only && (
                      <p className="text-rose-700 font-medium">⚠ Email contains non-ASCII characters — likely won't match what the client types.</p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(c.email);
                        toast.success("Copied stored email — paste it into Email/Name tab");
                      }}
                      className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                    >
                      Copy exact stored email
                    </button>
                    {c.pending_account && (
                      <div className="mt-1.5 pt-1.5 border-t border-slate-200 space-y-1">
                        <p className="text-[10px] text-slate-600 font-medium">
                          Attach a registered user account to this pending row
                        </p>
                        <div className="flex gap-1">
                          <Input
                            value={attachInputs[c.email] || ""}
                            onChange={(e) => setAttachFor(c.email, e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && attachUser(c.email)}
                            placeholder="actual registered email"
                            className="h-7 text-[10px]"
                            data-testid={`diagnostic-attach-input-${i}`}
                          />
                          <Button
                            size="sm"
                            className="h-7 text-[10px] shrink-0"
                            onClick={() => attachUser(c.email)}
                            disabled={busy || !(attachInputs[c.email] || "").trim()}
                            data-testid={`diagnostic-attach-btn-${i}`}
                          >
                            Attach
                          </Button>
                        </div>
                        <p className="text-[9px] text-slate-400 leading-tight">
                          Use when the client signed up with a slightly different email than what's on the case (typo / hidden whitespace). Overwrites this row's email with the real one and clears the pending flag.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
                {(caseRaw.clients || []).length === 0 && <p className="text-slate-400 italic">No clients on this case.</p>}
              </div>
            )}
          </>
        )}
        {tab === "lookup" && result && (
          <div className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 space-y-1">
            {result.mode === "partial" ? (
              <>
                <p className="font-semibold text-slate-700">Search results for "{result.query}"</p>
                <p className="text-slate-500">{result.hint}</p>
                {result.candidates?.length > 0 && (
                  <ul className="space-y-1 mt-1">
                    {result.candidates.map((c) => (
                      <li key={c.user_id} className="bg-white border border-slate-200 rounded p-1.5 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{c.name}</p>
                          <p className="text-[10px] text-slate-500 truncate">{c.email} <span className={c.role_is_client ? "text-emerald-700" : "text-rose-700"}>· role: {c.role || "(none)"}</span></p>
                        </div>
                        {!c.role_is_client && (
                          <Button
                            size="sm"
                            className="h-6 text-[10px] shrink-0"
                            onClick={async () => {
                              try {
                                const { data } = await API.post("/admin/user-lookup/fix-role", null, { params: { email: c.email } });
                                if (data.changed) {
                                  toast.success(`${c.name}: ${data.from} → ${data.to}`);
                                  setResult({ ...result, candidates: result.candidates.map((x) => x.user_id === c.user_id ? { ...x, role: "client", role_is_client: true } : x) });
                                }
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Fix failed");
                              }
                            }}
                            data-testid={`diagnostic-fix-${c.user_id}`}
                          >
                            Fix role
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : result.found ? (
              <>
                <p><span className="font-semibold text-slate-700">User exists:</span> <span className={result.role_is_client ? "text-emerald-700" : "text-rose-700"}>{result.name} ({result.role})</span></p>
                <p className="text-slate-500">Created: {result.created_at ? new Date(result.created_at).toLocaleString("en-GB") : "-"} · Active: {result.is_active ? "Yes" : "No"}</p>
                <p className="text-slate-500">Linked cases: {result.linked_cases_count} · Pending-only: {result.pending_only_cases_count}</p>
                <p className={result.role_is_client ? "text-emerald-700" : "text-rose-700 font-medium"}>{result.hint}</p>
                {!result.role_is_client && (
                  <Button size="sm" className="h-7 text-[11px] w-full" onClick={fixRole} disabled={busy} data-testid="diagnostic-fix-role-btn">
                    Fix role → client
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="text-rose-700 font-medium">Exact email not found</p>
                <p className="text-slate-600">{result.hint}</p>
                {result.pending_in_cases?.length > 0 && (
                  <ul className="list-disc list-inside pl-1 mt-1 space-y-0.5">
                    {result.pending_in_cases.map((c, i) => (
                      <li key={i} className="text-slate-500">{c.case_ref || c.title} (pending: {c.pending_account ? "yes" : "no"})</li>
                    ))}
                  </ul>
                )}
                {result._partial_fallback?.candidates?.length > 0 && (
                  <div className="border-t border-slate-200 pt-2 mt-2 space-y-1">
                    <p className="text-[10px] font-semibold text-slate-700 uppercase tracking-wide">
                      Possible matches (partial search on "{result._partial_fallback.query}")
                    </p>
                    <p className="text-[10px] text-slate-500">If one of these is your client, the stored email differs from what you typed (hidden whitespace / Cyrillic look-alike / typo).</p>
                    <ul className="space-y-1">
                      {result._partial_fallback.candidates.map((c) => (
                        <li key={c.user_id} className="bg-white border border-slate-200 rounded p-1.5 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{c.name}</p>
                            <p className="text-[10px] text-slate-500 truncate">{c.email} <span className={c.role_is_client ? "text-emerald-700" : "text-rose-700"}>· {c.role || "(no role)"}</span></p>
                          </div>
                          {!c.role_is_client && (
                            <Button
                              size="sm"
                              className="h-6 text-[10px] shrink-0"
                              onClick={async () => {
                                try {
                                  const { data } = await API.post("/admin/user-lookup/fix-role", null, { params: { email: c.email } });
                                  if (data.changed) toast.success(`${c.name}: ${data.from} → ${data.to}`);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Fix failed");
                                }
                              }}
                            >
                              Fix role
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

